import React from 'react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
};


// Classic Template
const ClassicTemplate = ({
  customer,
  company,
  invoiceNumber,
  invoiceDate,
  dueDate,
  items,
  notes,
  subtotal,
  taxRate,
  taxDetails,
  taxAmount,
  total,
  qrCode,
  className,
  footerData,
  roundingAdjustment,
  isQuote = false
}) => {
  return (
    <div className={`bg-white p-4 ${className}`}>
      <div className="row mb-5">
        <div className="col-6">
          <img src={company.logo} alt={company.name} className="img-fluid mb-3" style={{ maxHeight: '60px' }} />
          <h5 className="mb-1">{company.name}</h5>
          <p className="mb-1 small">{company.address.street}</p>
          <p className="mb-1 small">{company.address.city}, {company.address.state} {company.address.zipCode}</p>
          <p className="mb-1 small">{company.phone}</p>
          <p className="mb-0 small">{company.email}</p>
        </div>
        <div className="col-6 text-end">
          <h2 className="mb-4 text-uppercase text-primary">{isQuote ? 'Offert' : 'Faktura'}</h2>
          <p className="mb-1"><strong>{isQuote ? 'Offertnummer' : 'Fakturanummer'}:</strong> {invoiceNumber}</p>
          <p className="mb-1"><strong>{isQuote ? 'Offertdatum' : 'Fakturadatum'}:</strong> {invoiceDate}</p>
          <p className="mb-1"><strong>{isQuote ? 'Giltig till' : 'Förfallodatum'}:</strong> {dueDate}</p>
        </div>
      </div>

      <div className="row mb-5">
        <div className="col-6">
          <h5 className="mb-3">{isQuote ? 'Offert till:' : 'Faktureras till:'}</h5>
          <p className="mb-1"><strong>{customer.name}</strong></p>
          <p className="mb-1">{customer.contactName}</p>
          <p className="mb-1">{customer.address.street}</p>
          <p className="mb-1">{customer.address.city}, {customer.address.state} {customer.address.zipCode}</p>
          <p className="mb-1">{customer.address.country}</p>
          <p className="mb-1">{customer.phone}</p>
          <p className="mb-0">{customer.email}</p>
        </div>
      </div>

      <div className="table-responsive mb-4">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th scope="col" width="10%">#</th>
              <th scope="col" width="40%">Artikel & beskrivning</th>
              <th scope="col" width="15%" className="text-end">Pris</th>
              <th scope="col" width="10%" className="text-end">Antal</th>
              <th scope="col" width="10%" className="text-end">Moms</th>
              <th scope="col" width="15%" className="text-end">Belopp</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const itemTaxAmount = ((item.tax || 0) / 100) * (item.price * item.quantity);
              const totalAmount = (item.price * item.quantity) + itemTaxAmount;

              return (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>
                    <p className="mb-0"><strong>{item.name}</strong></p>
                    <p className="text-muted mb-0 small">{item.description}</p>
                  </td>
                  <td className="text-end">{formatCurrency(item.price)}</td>
                  <td className="text-end">{item.quantity}</td>
                  <td className="text-end">{item.tax || 0}%</td>
                  <td className="text-end">{formatCurrency(totalAmount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="row mb-5">
        <div className="col-md-6">
          <div className="bg-light p-3 rounded">
            <h5 className="mb-3">Anteckningar</h5>
            <p className="mb-0 small">{notes}</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="border p-3 rounded">
            <div className="d-flex justify-content-between mb-2">
              <span>Delsumma:</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>

            {/* Detailed tax breakdown */}
            {taxDetails && taxDetails.map((tax, index) => (
              <div key={`tax-${index}`} className="d-flex justify-content-between mb-2">
                <span>Moms ({tax.rate}%):</span>
                <span>{formatCurrency(tax.amount)}</span>
              </div>
            ))}

            {/* Rounding adjustment if applicable */}
            {roundingAdjustment !== 0 && (
              <div className="d-flex justify-content-between mb-2">
                <span>Avrundning:</span>
                <span>{formatCurrency(roundingAdjustment)}</span>
              </div>
            )}

            <div className="d-flex justify-content-between pt-2 border-top mt-2">
              <span className="h5">Totalt:</span>
              <span className="h5">{formatCurrency(total)}</span>
            </div>

            {/* QR Code */}
            <div className="text-center mt-3">
              {qrCode}
              <p className="small mt-2 text-muted">Skanna för att betala</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer for Print/PDF */}
      <div className="invoice-footer border-top pt-4">
        <div className="row">
          <div className="col-md-4 footer-column">
            {footerData?.col1?.split('\n').map((line, i) => (
              <p key={`footer-1-${i}`} className="mb-1 small">{line}</p>
            ))}
          </div>
          <div className="col-md-4 footer-column">
            {footerData?.col2?.split('\n').map((line, i) => (
              <p key={`footer-2-${i}`} className="mb-1 small">{line}</p>
            ))}
          </div>
          <div className="col-md-4 footer-column">
            {footerData?.col3?.split('\n').map((line, i) => (
              <p key={`footer-3-${i}`} className="mb-1 small">{line}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Modern Template
const ModernTemplate = ({
  customer,
  company,
  invoiceNumber,
  invoiceDate,
  dueDate,
  items,
  notes,
  subtotal,
  taxRate,
  taxDetails,
  taxAmount,
  total,
  qrCode,
  className,
  footerData,
  roundingAdjustment,
  isQuote = false
}) => {
  return (
    <div className={`bg-white ${className}`}>
      <div className="p-4 bg-primary text-white">
        <div className="row align-items-center">
          <div className="col-6">
            <h2 className="mb-0">{isQuote ? 'OFFERT' : 'FAKTURA'}</h2>
            <p className="mb-0">{invoiceNumber}</p>
          </div>
          <div className="col-6 text-end">
            <img src={company.logo} alt={company.name} className="img-fluid bg-white p-2 rounded" style={{ maxHeight: '50px' }} />
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="row mb-5">
          <div className="col-6">
            <h5 className="text-uppercase text-primary mb-3">Från</h5>
            <p className="mb-1"><strong>{company.name}</strong></p>
            <p className="mb-1 small">{company.address.street}</p>
            <p className="mb-1 small">{company.address.city}, {company.address.state} {company.address.zipCode}</p>
            <p className="mb-1 small">{company.phone}</p>
            <p className="mb-0 small">{company.email}</p>
          </div>
          <div className="col-6">
            <h5 className="text-uppercase text-primary mb-3">Till</h5>
            <p className="mb-1"><strong>{customer.name}</strong></p>
            <p className="mb-1 small">{customer.contactName}</p>
            <p className="mb-1 small">{customer.address.street}</p>
            <p className="mb-1 small">{customer.address.city}, {customer.address.state} {customer.address.zipCode}</p>
            <p className="mb-1 small">{customer.email}</p>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-md-4 mb-3 mb-md-0">
            <div className="p-3 border rounded text-center">
              <p className="small text-muted mb-0">{isQuote ? 'Offertdatum' : 'Fakturadatum'}</p>
              <p className="mb-0"><strong>{invoiceDate}</strong></p>
            </div>
          </div>
          <div className="col-md-4 mb-3 mb-md-0">
            <div className="p-3 border rounded text-center">
              <p className="small text-muted mb-0">{isQuote ? 'Giltig till' : 'Förfallodatum'}</p>
              <p className="mb-0"><strong>{dueDate}</strong></p>
            </div>
          </div>
          <div className="col-md-4">
            <div className="p-3 border rounded text-center bg-primary text-white">
              <p className="small mb-0">Att betala</p>
              <p className="h4 mb-0"><strong>{formatCurrency(total)}</strong></p>
            </div>
          </div>
        </div>

        <div className="table-responsive mb-4">
          <table className="table">
            <thead className="table-dark">
              <tr>
                <th scope="col" width="40%">Artikelbeskrivning</th>
                <th scope="col" width="15%" className="text-end">Pris</th>
                <th scope="col" width="10%" className="text-end">Antal</th>
                <th scope="col" width="10%" className="text-end">Moms</th>
                <th scope="col" width="25%" className="text-end">Belopp</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const itemTaxAmount = ((item.tax || 0) / 100) * (item.price * item.quantity);
                const totalAmount = (item.price * item.quantity) + itemTaxAmount;

                return (
                  <tr key={item.id}>
                    <td>
                      <p className="mb-0"><strong>{item.name}</strong></p>
                      <p className="text-muted mb-0 small">{item.description}</p>
                    </td>
                    <td className="text-end">{formatCurrency(item.price)}</td>
                    <td className="text-end">{item.quantity}</td>
                    <td className="text-end">{item.tax || 0}%</td>
                    <td className="text-end">{formatCurrency(totalAmount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="row mb-5">
          <div className="col-md-6 mb-3 mb-md-0">
            <div className="bg-light p-3 rounded h-100">
              <h5 className="mb-3">Anteckningar</h5>
              <p className="mb-0 small">{notes}</p>
            </div>
          </div>
          <div className="col-md-6">
            <div className="bg-light p-3 rounded">
              <div className="row">
                <div className="col-md-7">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Delsumma:</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>

                  {/* Detailed tax breakdown */}
                  {taxDetails && taxDetails.map((tax, index) => (
                    <div key={`tax-${index}`} className="d-flex justify-content-between mb-2">
                      <span>Moms ({tax.rate}%):</span>
                      <span>{formatCurrency(tax.amount)}</span>
                    </div>
                  ))}

                  {/* Rounding adjustment if applicable */}
                  {roundingAdjustment !== 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span>Avrundning:</span>
                      <span>{formatCurrency(roundingAdjustment)}</span>
                    </div>
                  )}

                  <div className="d-flex justify-content-between pt-2 border-top mt-2">
                    <span className="h5">Totalt:</span>
                    <span className="h5">{formatCurrency(total)}</span>
                  </div>
                </div>
                <div className="col-md-5 text-center">
                  {qrCode}
                  <p className="small mt-2 text-muted">Skanna för att betala</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer for Print/PDF */}
        <div className="invoice-footer border-top pt-3">
          <div className="row">
            <div className="col-md-4 footer-column">
              {footerData?.col1?.split('\n').map((line, i) => (
                <p key={`footer-1-${i}`} className="mb-1 small">{line}</p>
              ))}
            </div>
            <div className="col-md-4 footer-column">
              {footerData?.col2?.split('\n').map((line, i) => (
                <p key={`footer-2-${i}`} className="mb-1 small">{line}</p>
              ))}
            </div>
            <div className="col-md-4 footer-column">
              {footerData?.col3?.split('\n').map((line, i) => (
                <p key={`footer-3-${i}`} className="mb-1 small">{line}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Minimal Template
const MinimalTemplate = ({
  customer,
  company,
  invoiceNumber,
  invoiceDate,
  dueDate,
  items,
  notes,
  subtotal,
  taxRate,
  taxDetails,
  taxAmount,
  total,
  qrCode,
  className,
  footerData,
  roundingAdjustment,
  isQuote = false
}) => {
  return (
    <div className={`bg-white p-4 ${className}`}>
      <div className="row mb-5">
        <div className="col-6">
          <h1 className="display-6 mb-4">{isQuote ? 'Offert' : 'Faktura'}</h1>
          <p className="mb-1 small">{company.name}</p>
          <p className="mb-1 small">{company.address.street}</p>
          <p className="mb-1 small">{company.address.city}, {company.address.state} {company.address.zipCode}</p>
          <p className="mb-1 small">{company.phone}</p>
        </div>
        <div className="col-6 text-end">
          <p className="mb-1"><strong>{isQuote ? 'Offert' : 'Faktura'}:</strong> {invoiceNumber}</p>
          <p className="mb-1"><strong>Datum:</strong> {invoiceDate}</p>
          <p className="mb-1"><strong>{isQuote ? 'Giltig till' : 'Förfallodatum'}:</strong> {dueDate}</p>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <div className="border-top border-bottom py-2">
            <div className="row">
              <div className="col-6">
                <p className="mb-0 small text-uppercase text-muted">Faktureras till</p>
                <p className="mb-0"><strong>{customer.name}</strong></p>
                <p className="mb-0 small">{customer.address.street}</p>
                <p className="mb-0 small">{customer.address.city}, {customer.address.state} {customer.address.zipCode}</p>
              </div>
              <div className="col-6 text-end">
                <p className="mb-0 small text-uppercase text-muted">Att betala</p>
                <p className="mb-0 h3">{formatCurrency(total)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="table-responsive mb-4">
        <table className="table table-borderless">
          <thead>
            <tr className="border-bottom">
              <th scope="col" width="50%">Beskrivning</th>
              <th scope="col" width="15%" className="text-end">Pris</th>
              <th scope="col" width="10%" className="text-end">Antal</th>
              <th scope="col" width="25%" className="text-end">Belopp</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const itemSubtotal = item.price * item.quantity;

              return (
                <tr key={item.id} className="border-bottom">
                  <td>
                    <p className="mb-0">{item.name}</p>
                    <p className="text-muted mb-0 small">{item.description}</p>
                  </td>
                  <td className="text-end">{formatCurrency(item.price)}</td>
                  <td className="text-end">{item.quantity}</td>
                  <td className="text-end">{formatCurrency(itemSubtotal)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="row mb-5">
        <div className="col-md-6 small">
          <div className="mb-3">
            <p className="mb-1 text-uppercase text-muted small">Anteckningar</p>
            <p className="mb-0">{notes}</p>
          </div>
          <div>
            <p className="mb-1 text-uppercase text-muted small">Betalningsuppgifter</p>
            <p className="mb-0">Vänligen ange {isQuote ? 'offertnummer' : 'fakturanummer'} vid betalning</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
            <div className="col-md-7">
              <div className="text-end">
                <div className="d-inline-block text-end" style={{ minWidth: '200px' }}>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Delsumma</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>

                  {/* Detailed tax breakdown */}
                  {taxDetails && taxDetails.map((tax, index) => (
                    <div key={`tax-${index}`} className="d-flex justify-content-between mb-2">
                      <span>Moms ({tax.rate}%)</span>
                      <span>{formatCurrency(tax.amount)}</span>
                    </div>
                  ))}

                  {/* Rounding adjustment if applicable */}
                  {roundingAdjustment !== 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span>Avrundning</span>
                      <span>{formatCurrency(roundingAdjustment)}</span>
                    </div>
                  )}

                  <div className="d-flex justify-content-between pt-2 border-top">
                    <span><strong>Totalt</strong></span>
                    <span><strong>{formatCurrency(total)}</strong></span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-5 text-center">
              {qrCode}
              <p className="small mt-2 text-muted">Skanna för att betala</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer for Print/PDF */}
      <div className="invoice-footer pt-3 border-top small text-muted">
        <div className="row">
          <div className="col-md-4 footer-column">
            {footerData?.col1?.split('\n').map((line, i) => (
              <p key={`footer-1-${i}`} className="mb-1 small">{line}</p>
            ))}
          </div>
          <div className="col-md-4 footer-column">
            {footerData?.col2?.split('\n').map((line, i) => (
              <p key={`footer-2-${i}`} className="mb-1 small">{line}</p>
            ))}
          </div>
          <div className="col-md-4 footer-column">
            {footerData?.col3?.split('\n').map((line, i) => (
              <p key={`footer-3-${i}`} className="mb-1 small">{line}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Define the list of available templates with updated thumbnails
export const invoiceTemplatesJs = [
  {
    id: "classic",
    name: "Klassisk",
    description: "Traditionell offert med ren och professionell layout",
    thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='280' height='210' viewBox='0 0 280 210' fill='none'%3E%3Crect width='280' height='210' fill='white'/%3E%3Crect x='20' y='20' width='240' height='20' fill='%23F3F4F6'/%3E%3Crect x='20' y='50' width='240' height='2' fill='%23E5E7EB'/%3E%3Crect x='20' y='60' width='120' height='40' fill='%23F9FAFB'/%3E%3Crect x='150' y='60' width='110' height='40' fill='%23F9FAFB'/%3E%3Crect x='20' y='110' width='240' height='60' fill='%23F9FAFB'/%3E%3Crect x='20' y='180' width='240' height='10' fill='%23F3F4F6'/%3E%3C/svg%3E",
    render: (props) => <ClassicTemplate {...props} />
  },
  {
    id: "modern",
    name: "Modern",
    description: "Samtida design med eleganta sektioner och färgaccenter",
    thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='280' height='210' viewBox='0 0 280 210' fill='none'%3E%3Crect width='280' height='210' fill='white'/%3E%3Crect x='0' y='0' width='280' height='40' fill='%23F3F4F6'/%3E%3Crect x='20' y='60' width='110' height='60' fill='%23F9FAFB'/%3E%3Crect x='140' y='60' width='120' height='60' fill='%23F9FAFB'/%3E%3Crect x='20' y='130' width='240' height='40' fill='%23F9FAFB'/%3E%3Crect x='0' y='180' width='280' height='30' fill='%23F3F4F6'/%3E%3C/svg%3E",
    render: (props) => <ModernTemplate {...props} />
  },
  {
    id: "minimal",
    name: "Minimalistisk",
    description: "Ren, enkel design med fokus på innehåll och väsentlig information",
    thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='280' height='210' viewBox='0 0 280 210' fill='none'%3E%3Crect width='280' height='210' fill='white'/%3E%3Crect x='20' y='20' width='100' height='10' fill='%23F3F4F6'/%3E%3Crect x='20' y='40' width='100' height='40' fill='%23FFFFFF'/%3E%3Crect x='160' y='40' width='100' height='40' fill='%23FFFFFF'/%3E%3Crect x='20' y='100' width='240' height='1' fill='%23E5E7EB'/%3E%3Crect x='20' y='110' width='240' height='60' fill='%23FFFFFF'/%3E%3Crect x='140' y='180' width='120' height='10' fill='%23F9FAFB'/%3E%3C/svg%3E",
    render: (props) => <MinimalTemplate {...props} />
  }
];
